/**
 * W1/W4/W5 — default workbench registry bootstrap
 * WB4-CP1 — Filter / Create palette registry seeds
 */

import { registerCommands } from '@shared/business-workbench/command-registry';
import { buildPaletteDomainActionCommandEntries } from '@shared/business-workbench/workbench-palette-domain-action-seeds';
import { buildRoleCompositionPaletteCommandEntries } from '@shared/business-workbench/workbench-role-composition-palette-seeds';
import { ROLES } from '@shared/types/roles';
import { ensureFlowWorkbenchActionsRegistered } from '../components/workbench/FlowBindingPanel';

let bootstrapped = false;

export function ensureWorkbenchBootstrap(): void {
  if (bootstrapped) {
    return;
  }

  ensureFlowWorkbenchActionsRegistered([
    { flowTemplateId: 'generic_intake', label: 'Flow 시작' },
    { flowTemplateId: 'order_intake', label: '주문 시작' },
  ]);

  registerCommands([
    {
      id: 'nav-workspace',
      label: 'Workspace 열기',
      keywords: ['workspace', '작업대'],
      kind: 'navigate',
      route: '/workspace',
      planes: ['business'],
      priority: 90,
    },
    {
      id: 'nav-customers',
      label: '거래처 목록',
      keywords: ['customer', 'external', '거래처'],
      kind: 'navigate',
      route: '/entity/external',
      paletteGroup: 'go',
      planes: ['business'],
      priority: 88,
    },
    {
      id: 'nav-inventory-stock',
      label: '재고 현황',
      keywords: ['inventory', 'stock', '재고'],
      kind: 'navigate',
      route: '/inventory/stock',
      paletteGroup: 'go',
      planes: ['business'],
      priority: 87,
    },
    {
      id: 'nav-approvals-pending',
      label: '대기 승인',
      keywords: ['approval', 'pending', '승인'],
      kind: 'navigate',
      route: '/approvals/pending',
      paletteGroup: 'go',
      planes: ['business'],
      priority: 86,
    },
    {
      id: 'nav-finance-state',
      label: '재무 현황',
      keywords: ['finance', 'state', '재무'],
      kind: 'navigate',
      route: '/finance/state',
      paletteGroup: 'go',
      planes: ['business'],
      priority: 85,
    },
    {
      id: 'nav-knowledge-report',
      label: '문서 목록',
      keywords: ['document', 'knowledge', 'report', '문서'],
      kind: 'navigate',
      route: '/knowledge/report',
      paletteGroup: 'go',
      planes: ['business'],
      priority: 84,
    },
    {
      id: 'nav-supplier',
      label: '공급처 목록',
      keywords: ['supplier', '공급처', '공급', '조달'],
      kind: 'navigate',
      route: '/entity/supplier',
      paletteGroup: 'go',
      planes: ['business'],
      priority: 83,
    },
    {
      id: 'nav-item-sellable',
      label: '판매 가능 항목',
      keywords: ['sellable', 'item', '판매', '상품'],
      kind: 'navigate',
      route: '/item/sellable',
      paletteGroup: 'go',
      planes: ['business'],
      priority: 82,
    },
    {
      id: 'nav-manufacturing-production-order',
      label: '생산 오더',
      keywords: ['production', 'order', 'manufacturing', '생산'],
      kind: 'navigate',
      route: '/manufacturing/production-order',
      paletteGroup: 'go',
      planes: ['business'],
      priority: 81,
    },
    {
      id: 'nav-warehouse-picking-list',
      label: '피킹 목록',
      keywords: ['picking', 'warehouse', '피킹', '창고'],
      kind: 'navigate',
      route: '/warehouse/picking-list',
      paletteGroup: 'go',
      planes: ['business'],
      priority: 80,
    },
    {
      id: 'nav-space-physical',
      label: '물리 공간',
      keywords: ['space', 'physical', '공간', '장소'],
      kind: 'navigate',
      route: '/space/physical',
      paletteGroup: 'go',
      planes: ['business'],
      priority: 79,
    },
    {
      id: 'nav-calendar-event',
      label: '캘린더 이벤트',
      keywords: ['calendar', 'event', '일정', '캘린더'],
      kind: 'navigate',
      route: '/calendar/event',
      paletteGroup: 'go',
      planes: ['business'],
      priority: 78,
    },
    {
      id: 'nav-flow-dispatch',
      label: '출고 대기 큐',
      keywords: ['dispatch', '출고', '배송', 'flow'],
      kind: 'navigate',
      route: '/flow/dispatch',
      paletteGroup: 'go',
      planes: ['business'],
      priority: 77,
    },
    {
      id: 'nav-event-flow-definition',
      label: 'Flow 정의',
      keywords: ['flow', 'definition', '이벤트', '정의'],
      kind: 'navigate',
      route: '/event/flow-definition',
      paletteGroup: 'go',
      planes: ['business'],
      priority: 76,
    },
    {
      id: 'nav-knowledge-report-fields-layout',
      label: '문서 필드·레이아웃',
      keywords: ['configure', 'fields', 'layout', '필드', '레이아웃', '문서'],
      kind: 'navigate',
      route: '/knowledge/report/fields-and-layout',
      paletteGroup: 'go',
      planes: ['business'],
      priority: 75,
    },
    {
      id: 'nav-assignments',
      label: 'Operation Assignment Board',
      keywords: ['assign', '배정', 'board'],
      kind: 'navigate',
      route: '/workspace/assignments',
      planes: ['business'],
      roles: [ROLES.OWNER, ROLES.OPERATIONS_MANAGER],
      priority: 80,
    },
    ...buildRoleCompositionPaletteCommandEntries(),
    {
      id: 'cmd-start-flow',
      label: 'Flow 시작',
      keywords: ['flow', 'start'],
      kind: 'action',
      actionRef: 'flow:start:generic_intake',
      paletteGroup: 'flow',
      planes: ['business'],
      priority: 70,
    },
    {
      id: 'filter-active-only',
      label: '활성 항목만 보기',
      keywords: ['filter', 'active', '필터'],
      kind: 'search',
      paletteGroup: 'filter',
      planes: ['business'],
      priority: 60,
    },
    {
      id: 'filter-overdue-ar',
      label: '연체 AR 필터',
      keywords: ['filter', 'overdue', 'ar'],
      kind: 'action',
      actionRef: 'filter:overdue-ar',
      paletteGroup: 'filter',
      planes: ['business'],
      priority: 55,
    },
    {
      id: 'create-via-flow',
      label: '새 Flow로 생성',
      keywords: ['create', 'flow', '생성'],
      kind: 'action',
      actionRef: 'flow:start:generic_intake',
      paletteGroup: 'create',
      planes: ['business'],
      priority: 50,
    },
    {
      id: 'create-collection-follow-up',
      label: '수금 팔로업 생성',
      keywords: ['collection', 'follow-up', '수금'],
      kind: 'action',
      actionRef: 'workbench:intent:collect_payment',
      paletteGroup: 'create',
      planes: ['business'],
      priority: 49,
    },
    {
      id: 'filter-overdue-receivables',
      label: '연체 채권 필터',
      keywords: ['filter', 'receivables', '연체'],
      kind: 'action',
      actionRef: 'filter:overdue-receivables',
      paletteGroup: 'filter',
      planes: ['business'],
      priority: 48,
    },
    {
      id: 'create-order-intake',
      label: '주문 접수 Flow',
      keywords: ['order', 'intake', '주문'],
      kind: 'action',
      actionRef: 'flow:start:order_intake',
      paletteGroup: 'create',
      planes: ['business'],
      priority: 47,
    },
    {
      id: 'filter-pending-orders',
      label: '대기 주문 필터',
      keywords: ['filter', 'pending', 'orders'],
      kind: 'action',
      actionRef: 'filter:pending-orders',
      paletteGroup: 'filter',
      planes: ['business'],
      priority: 46,
    },
    {
      id: 'create-assignment-flow',
      label: '배정 Flow 시작',
      keywords: ['task', 'assignment', '배정'],
      kind: 'action',
      actionRef: 'flow:start:flow.execute',
      paletteGroup: 'create',
      planes: ['business'],
      priority: 45,
    },
    {
      id: 'filter-pending-tasks',
      label: '대기 작업 필터',
      keywords: ['filter', 'pending', 'tasks'],
      kind: 'action',
      actionRef: 'filter:pending-tasks',
      paletteGroup: 'filter',
      planes: ['business'],
      priority: 44,
    },
    {
      id: 'explain:blocked-action-global',
      label: '차단된 작업 설명',
      keywords: ['explain', 'blocked', 'why', '설명', '차단'],
      kind: 'explain',
      paletteGroup: 'explain',
      sourceActionId: 'blocked-action-global',
      planes: ['business'],
      priority: 41,
    },
    {
      id: 'explain:focus-selection',
      label: '선택 객체 작업 설명',
      keywords: ['explain', 'focus', 'selection', '선택', '설명'],
      kind: 'explain',
      paletteGroup: 'explain',
      sourceActionId: 'focus-selection',
      planes: ['business'],
      businessDomains: ['order', 'sales_order', 'collection', 'task_execution', 'finance_state'],
      priority: 40,
    },
    ...buildPaletteDomainActionCommandEntries(),
  ]);

  bootstrapped = true;
}
